import api from '@services/api.service'

export type OcrKind = 'listener' | 'company'
export type OcrSplit = 'train' | 'validation'
export type OcrFileStatus = 'pending' | 'completed'
export interface OcrRegion { x: number; y: number; width: number; height: number }
export interface OcrFile { id: number; kind: OcrKind; name: string; ownerLabel?: string; completedAt?: string; sampleCount?: number }
export interface OcrSample { id: string; text: string; split: OcrSplit; sourceName?: string; page?: number }
export interface OcrLine {id?: string; page: number; region: OcrRegion}
export interface OcrLineDraft {fileId: number; kind: OcrKind; revision: number; split: OcrSplit; pages: number; lines: OcrLine[]; reviewedPages: number[]; saved: boolean; locked?: boolean; sourceChanged?: boolean; message?: string}
export interface OcrCharacter { id: string; index: number; page: number; region: OcrRegion; contextRegion?: OcrRegion; previousText?: string }
export interface OcrCharacterQueue {
    queueId?: string; status: 'preparing' | 'ready' | 'failed' | 'completed'
    total: number; reviewed: number; skipped: number; split: OcrSplit; pages?: number; message?: string
    current?: OcrCharacter | null
    previous?: {id: string; revision: number; state: 'labeled' | 'skipped'; text?: string} | null
}
export interface OcrStatus {
    runtime: {available: boolean; message?: string}
    dataset: {train: number; validation: number; total: number}
    model: {initialized: boolean; epoch?: number; trainedAt?: string; validationCer?: number}
    job: null | {id: string; status: string; epoch?: number; epochs?: number; loss?: number; valCer?: number; valAccuracy?: number; charAccuracy?: number; message?: string}
}
export interface OcrBulkStatus {
    version: 1; id: string; status: 'preparing' | 'training' | 'completed' | 'stopping' | 'stopped' | 'failed'
    startedAt: string; updatedAt: string; currentStage?: string; message?: string
    counts: {total: number; examined: number; trained: number; failed: number; unsupported: number; empty: number; pending: number}
}
export interface OcrStorage {
    status: 'calculating' | 'ready' | 'failed'
    modelBytes: number | null; totalBytes: number | null; updatedAt: string | null; message?: string
}

export const ocrErrorMessage = (error: any): string => (
    error?.response?.data?.message || (error?.message && !/^Request failed|^Network Error|^timeout of/.test(error.message) ? error.message : '') || 'Не удалось выполнить запрос. Повторите попытку.'
)

async function blobData(request: Promise<any>) {
    try {return await request}
    catch (error) {
        const failure = error as any
        if (failure?.response?.data instanceof Blob) {
            try {
                const blob = failure.response.data as Blob
                const raw = typeof blob.text === 'function' ? await blob.text() : await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader()
                    reader.onload = () => resolve(String(reader.result || ''))
                    reader.onerror = () => reject(reader.error)
                    reader.readAsText(blob)
                })
                const details = JSON.parse(raw)
                if (typeof details?.message === 'string') failure.response.data = details
            } catch {}
        }
        throw error
    }
}

async function data<T>(request: Promise<any>): Promise<T> {
    const response = await request
    const envelope = response.data
    if (envelope?.status !== 200) throw new Error(envelope?.message || 'Не удалось выполнить запрос OCR.')
    return envelope.data as T
}

export const ocrApi = {
    status: (signal?: AbortSignal) => data<OcrStatus>(api.get('/ocr/status', {signal})),
    storage: (signal?: AbortSignal) => data<OcrStorage>(api.get('/ocr/storage', {signal})),
    refreshStorage: (signal?: AbortSignal) => data<OcrStorage>(api.post('/ocr/storage/refresh', {}, {signal})),
    bulkStatus: (signal?: AbortSignal) => data<OcrBulkStatus | null>(api.get('/ocr/bulk/status', {signal})),
    bulkStop: () => data<OcrBulkStatus | null>(api.post('/ocr/bulk/stop', {})),
    files: (params: {query: string; page: number; pageSize: number; kind: OcrKind; status: OcrFileStatus}, signal?: AbortSignal) =>
        data<{items: OcrFile[]; totalCount: number; page: number}>(api.get('/ocr/files', {params, signal})),
    complete: (file: OcrFile) => data<unknown>(api.post(`/ocr/files/${file.kind}/${file.id}/complete`, {})),
    reopen: (file: OcrFile) => data<unknown>(api.post(`/ocr/files/${file.kind}/${file.id}/reopen`, {})),
    characters: (file: OcrFile, signal?: AbortSignal) => data<OcrCharacterQueue>(api.get(`/ocr/files/${file.kind}/${file.id}/characters`, {signal})),
    lines: (file: OcrFile, signal?: AbortSignal) => data<OcrLineDraft>(api.get(`/ocr/files/${file.kind}/${file.id}/lines`, {signal})),
    saveLines: (file: OcrFile, draft: {expectedRevision: number; split: OcrSplit; lines: OcrLine[]; reviewedPages: number[]}) => data<OcrLineDraft>(api.put(`/ocr/files/${file.kind}/${file.id}/lines`, draft)),
    prepareCharacters: (file: OcrFile, split: OcrSplit, signal?: AbortSignal, linesRevision?: number) => data<OcrCharacterQueue>(api.post(`/ocr/files/${file.kind}/${file.id}/characters/prepare`, {split, ...(linesRevision !== undefined ? {linesRevision} : {})}, {signal})),
    characterImage: async (id: string, context = false, signal?: AbortSignal) => (await blobData(api.get(`/ocr/characters/${encodeURIComponent(id)}/${context ? 'context' : 'image'}`, {responseType: 'blob', signal}))).data as Blob,
    labelCharacter: (id: string, text: string) => data<OcrCharacterQueue>(api.post(`/ocr/characters/${encodeURIComponent(id)}/label`, {text})),
    skipCharacter: (id: string) => data<OcrCharacterQueue>(api.post(`/ocr/characters/${encodeURIComponent(id)}/skip`, {})),
    backCharacter: (id: string, expectedRevision: number) => data<OcrCharacterQueue>(api.post(`/ocr/characters/${encodeURIComponent(id)}/back`, {expectedRevision})),
    adjustCharacter: (id: string, region: OcrRegion) => data<OcrCharacterQueue>(api.post(`/ocr/characters/${encodeURIComponent(id)}/adjust`, {region})),
    mergeCharacter: (id: string) => data<OcrCharacterQueue>(api.post(`/ocr/characters/${encodeURIComponent(id)}/merge-next`, {})),
    splitCharacter: (id: string, at: number) => data<OcrCharacterQueue>(api.post(`/ocr/characters/${encodeURIComponent(id)}/split`, {at})),
    insertCharacter: (file: OcrFile, page: number, region: OcrRegion) => data<OcrCharacterQueue>(api.post(`/ocr/files/${file.kind}/${file.id}/characters/insert`, {page, region})),
    samples: (page: number, signal?: AbortSignal) => data<{items: OcrSample[]; totalCount: number}>(api.get('/ocr/samples', {params: {page, pageSize: 12}, signal})),
    preview: async (file: OcrFile, page: number, signal?: AbortSignal) => {
        const response = await blobData(api.get(`/ocr/files/${file.kind}/${file.id}/preview`, {params: {page}, responseType: 'blob', signal}))
        return {blob: response.data as Blob, pages: Math.max(1, Number(response.headers['x-ocr-page-count']) || 1)}
    },
    image: async (id: string, signal?: AbortSignal) => (await blobData(api.get(`/ocr/samples/${encodeURIComponent(id)}/image`, {responseType: 'blob', signal}))).data as Blob,
    save: (sample: {fileId: number; kind: OcrKind; page: number; region: OcrRegion; text: string; split: OcrSplit}) => data<OcrSample>(api.post('/ocr/samples', sample)),
    remove: (id: string) => data<unknown>(api.delete(`/ocr/samples/${encodeURIComponent(id)}`)),
    train: (options: {epochs: number; batchSize: number; learningRate: number; mode: 'scratch' | 'continue'}) => data<unknown>(api.post('/ocr/train', options)),
    stop: () => data<unknown>(api.post('/ocr/train/stop')),
    predict: (sampleId: string) => data<{text: string}>(api.post('/ocr/predict', {sampleId})),
}
